import { computed, type Ref } from 'vue'
import { findLatestAkiValueByKey } from '../components/molecules/VitalInfoTable.utils'
import { VitalSignsKeys } from '../models/VitalSignsKeys'
import { type AkiParamsItem, LaboratoryTypeKey } from '@/generated'
import { getVitalDateLabel } from '@/util/getVitalDateLabel'

export type PatientVitalsElement = {
  displayValue: string
  displayDate: string
}

export type SystolicPressureVitalsElement = PatientVitalsElement & {
  hasMeasurement: boolean
}

export function usePatientAkiParams(params: Ref<AkiParamsItem[]>) {
  const INV_BP = computed<SystolicPressureVitalsElement>(() => {
    const sbp = findLatestAkiValueByKey(params.value, VitalSignsKeys.INV_S_BP)
    const dbp = findLatestAkiValueByKey(params.value, VitalSignsKeys.INV_D_BP)
    const mbp = findLatestAkiValueByKey(params.value, VitalSignsKeys.INV_M_BP)

    const hasRequiredMeasurements = [sbp, dbp].every((v) => v.hasMeasurement)
    const latestDate = hasRequiredMeasurements
      ? [sbp, dbp, mbp]
          .map((measurement) => measurement.measurementDate)
          .filter((v) => v != '–')
          .reduce((a, b) => (a > b ? a : b))
      : '–'
    const mbpValue = mbp.hasMeasurement ? mbp.value : '–'

    return {
      displayValue: hasRequiredMeasurements ? `${sbp.value}/${dbp.value} (${mbpValue})` : '–',
      hasMeasurement: hasRequiredMeasurements,
      displayDate: getVitalDateLabel(latestDate)
    }
  })

  const NON_INV_BP = computed<SystolicPressureVitalsElement>(() => {
    const sbp = findLatestAkiValueByKey(params.value, VitalSignsKeys.NON_INV_S_BP)
    const dbp = findLatestAkiValueByKey(params.value, VitalSignsKeys.NON_INV_D_BP)
    const mbp = findLatestAkiValueByKey(params.value, VitalSignsKeys.NON_INV_M_BP)

    const hasRequiredMeasurements = [sbp, dbp].every((v) => v.hasMeasurement)
    const latestDate = hasRequiredMeasurements
      ? [sbp, dbp, mbp]
          .map((measurement) => measurement.measurementDate)
          .filter((v) => v != '–')
          .reduce((a, b) => (a > b ? a : b))
      : '–'
    const mbpValue = mbp.hasMeasurement ? mbp.value : '–'

    return {
      displayValue: hasRequiredMeasurements ? `${sbp.value}/${dbp.value} (${mbpValue})` : '–',
      hasMeasurement: hasRequiredMeasurements,
      displayDate: getVitalDateLabel(latestDate)
    }
  })

  const HEART_RATE = computed<PatientVitalsElement>(() => {
    const latestValue = findLatestAkiValueByKey(params.value, VitalSignsKeys.HEART_RATE)
    return {
      displayValue: latestValue.value,
      displayDate: getVitalDateLabel(latestValue.measurementDate)
    }
  })
  const RR = computed<PatientVitalsElement>(() => {
    const latestValue = findLatestAkiValueByKey(params.value, VitalSignsKeys.RR)
    return {
      displayValue: latestValue.value,
      displayDate: getVitalDateLabel(latestValue.measurementDate)
    }
  })
  const SPO2 = computed<PatientVitalsElement>(() => {
    const latestValue = findLatestAkiValueByKey(params.value, VitalSignsKeys.SPO2)
    return {
      displayValue: latestValue.value,
      displayDate: getVitalDateLabel(latestValue.measurementDate)
    }
  })
  const CREATININE = computed<PatientVitalsElement>(() => {
    const latestValue = findLatestAkiValueByKey(params.value, VitalSignsKeys.CREATININE)
    return {
      displayValue: latestValue.value,
      displayDate: getVitalDateLabel(latestValue.measurementDate)
    }
  })

  const UREA = computed<PatientVitalsElement>(() => {
    const latestValue = findLatestAkiValueByKey(params.value, LaboratoryTypeKey.HARN)
    return {
      displayValue: latestValue.value,
      displayDate: getVitalDateLabel(latestValue.measurementDate)
    }
  })

  return {
    INV_BP,
    NON_INV_BP,
    HEART_RATE,
    RR,
    SPO2,
    CREATININE,
    UREA
  }
}
